import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import MobileHeader from "../../components/MobileHeader";
import BottomNav from "../../components/BottomNav";
import AddressSearch from "../../components/AddressSearch";
import MapView from "../../components/MapView";
import AiInsightCard from "../../components/AiInsightCard";
import { api, DRC_CENTER, DRC_MAP_ZOOM, CITY_MAP_ZOOM, formatApiError, formatCDF } from "../../lib/api";
import { getPreciseLocation } from "../../lib/preciseLocation";
import { reverseGeocode } from "../../lib/geo";
import { useAuth } from "../../context/AuthContext";
import { Badge, Button, Card, Input, Label, StatusBadge } from "../../components/ui-kit";
import {
  ArrowLeft,
  Car,
  Crosshair,
  FloppyDisk,
  ForkKnife,
  MapPin,
  MagnifyingGlass,
  Minus,
  Motorcycle,
  Package,
  Phone,
  Plus,
  ShoppingCart,
  Storefront,
  Trash,
  X,
} from "@phosphor-icons/react";

const TYPE_CONFIG = {
  restaurants: {
    apiType: "restaurant",
    title: "Restaurants",
    subtitle: "Commandez un repas, Nexora envoie un livreur apres confirmation.",
    icon: ForkKnife,
    empty: "Aucun restaurant actif dans cette ville.",
  },
  supermarkets: {
    apiType: "supermarket",
    title: "Supermarches",
    subtitle: "Faites vos courses, un chauffeur/livreur livre a votre adresse.",
    icon: Storefront,
    empty: "Aucun supermarche actif dans cette ville.",
  },
  pharmacies: {
    apiType: "pharmacy",
    title: "Pharmacies",
    subtitle: "Commandez les produits de pharmacie disponibles avec livraison locale.",
    icon: Storefront,
    empty: "Aucune pharmacie active dans cette ville.",
  },
};

const RECENT_PLACES_KEY = "nexora-recent-places";

function qtyInCart(cart, productId) {
  return Number(cart[productId] || 0);
}

function cleanPublicPhone(phone) {
  const value = String(phone || "").trim();
  return value && !value.startsWith("oauth:") ? value : "";
}

function PaymentProofBox({ order, uploading, autoPaying, onUpload, onAutoPay }) {
  const [reference, setReference] = useState("");
  const [file, setFile] = useState(null);
  if (order.payment_method === "cash") return null;
  const confirmed = order.payment_status === "CONFIRMED";
  const rejected = order.payment_status === "REJECTED";
  const providerPending = order.payment_status === "PROVIDER_PENDING";
  return (
    <div className="mt-3 rounded-2xl border border-white/10 bg-white/5 p-3">
      <div className="text-[10px] font-black uppercase tracking-widest text-primary">Preuve paiement {String(order.payment_method || "").toUpperCase()}</div>
      <div className="mt-1 text-xs text-muted-foreground">
        Numero partenaire: <span className="font-bold text-foreground">{order.merchant_payment_phone || "Non configure"}</span>
      </div>
      {order.payment_proof_url && (
        <a href={order.payment_proof_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex rounded-full bg-white/10 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-primary">
          Voir ma capture
        </a>
      )}
      {confirmed ? (
        <p className="mt-2 text-xs text-emerald-400">Paiement confirme. Le partenaire peut preparer la commande.</p>
      ) : (
        <div className="mt-3 grid gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={autoPaying || !order.merchant_payment_phone}
            onClick={() => onAutoPay(order)}
            className="w-full"
          >
            {autoPaying ? "Paiement..." : "Payer automatiquement"}
          </Button>
          {providerPending && (
            <p className="text-xs text-primary">Demande envoyee au provider. La commande sera debloquee apres confirmation automatique.</p>
          )}
          {rejected && <p className="text-xs text-destructive">La derniere preuve a ete refusee. Renvoyez une capture plus lisible.</p>}
          <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Reference transaction optionnelle" />
          <input
            type="file"
            accept="image/*,.pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full rounded-xl border border-white/10 bg-background px-3 py-2 text-xs"
          />
          <Button disabled={!file || uploading} onClick={() => onUpload(order.id, file, reference)} className="w-full">
            {uploading ? "Envoi..." : "Envoyer capture"}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function Marketplace() {
  const { type = "restaurants" } = useParams();
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.restaurants;
  const Icon = cfg.icon;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedMerchantId, setSelectedMerchantId] = useState("");
  const [orders, setOrders] = useState([]);
  const [cart, setCart] = useState({});
  const [deliveryAddress, setDeliveryAddress] = useState(null);
  const [recipientName, setRecipientName] = useState(user?.name || "");
  const [recipientPhone, setRecipientPhone] = useState(() => cleanPublicPhone(user?.phone));
  const [note, setNote] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [courierVehicleType, setCourierVehicleType] = useState("moto");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [proofUploading, setProofUploading] = useState("");
  const [autoPaying, setAutoPaying] = useState("");

  const currentCity = useMemo(() => cities.find((city) => city.id === user?.city_id), [cities, user?.city_id]);
  const cityCenter = useMemo(() => currentCity ? { lat: currentCity.lat, lng: currentCity.lng } : DRC_CENTER, [currentCity]);
  const selectedMerchant = useMemo(
    () => merchants.find((merchant) => merchant.id === selectedMerchantId) || null,
    [merchants, selectedMerchantId],
  );
  const mapCenter = useMemo(() => {
    if (!currentCity) return DRC_CENTER;
    if (deliveryAddress) return deliveryAddress;
    if (selectedMerchant) return { lat: selectedMerchant.lat, lng: selectedMerchant.lng };
    return cityCenter;
  }, [cityCenter, currentCity, deliveryAddress, selectedMerchant]);
  const mapZoom = currentCity ? CITY_MAP_ZOOM : DRC_MAP_ZOOM;

  const preciseCurrentAddress = useCallback(async () => {
    const loc = await getPreciseLocation();
    const place = await reverseGeocode(loc.lat, loc.lng);
    return {
      ...loc,
      address: place?.label || `${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)}`,
      label: place?.label,
      short: place?.short,
      map_data: place || null,
    };
  }, []);

  const cartLines = useMemo(() => {
    return products
      .filter((product) => qtyInCart(cart, product.id) > 0)
      .map((product) => ({
        ...product,
        quantity: qtyInCart(cart, product.id),
        line_total: Number(product.price || 0) * qtyInCart(cart, product.id),
      }));
  }, [cart, products]);

  const subtotal = useMemo(
    () => cartLines.reduce((sum, item) => sum + item.line_total, 0),
    [cartLines],
  );
  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    return products.filter((product) =>
      (categoryFilter === "all" || (product.category || "General") === categoryFilter) &&
      (!q || [product.name, product.description, product.category, product.merchant_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q)))
    );
  }, [categoryFilter, productSearch, products]);
  const productCategories = useMemo(() => {
    const categories = products.map((product) => product.category || "General").filter(Boolean);
    return Array.from(new Set(categories)).sort((a, b) => a.localeCompare(b));
  }, [products]);
  const paymentPhone = useMemo(() => {
    if (!selectedMerchant || paymentMethod === "cash") return "";
    return selectedMerchant[`${paymentMethod}_phone`] || "";
  }, [paymentMethod, selectedMerchant]);

  const loadOrders = useCallback(() => {
    api.get("/merchant-orders")
      .then(({ data }) => setOrders(Array.isArray(data) ? data : []))
      .catch(() => setOrders([]));
  }, []);

  const loadMerchants = useCallback(() => {
    if (!currentCity) return;
    api.get("/merchants", { params: { type: cfg.apiType, city_id: currentCity.id } })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setMerchants(list);
      })
      .catch((err) => toast.error(formatApiError(err)));
  }, [cfg.apiType, currentCity]);

  useEffect(() => {
    api.get("/cities").then(({ data }) => setCities(Array.isArray(data) ? data : [])).catch(() => setCities([]));
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    setRecipientName((current) => current || user?.name || "");
    setRecipientPhone((current) => current || cleanPublicPhone(user?.phone));
  }, [user?.name, user?.phone]);

  useEffect(() => {
    let cancelled = false;
    if (!currentCity || deliveryAddress) return undefined;
    preciseCurrentAddress()
      .then((location) => {
        if (!cancelled) setDeliveryAddress(location);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentCity, deliveryAddress, preciseCurrentAddress]);

  useEffect(() => {
    loadMerchants();
  }, [loadMerchants]);

  useEffect(() => {
    api.get("/marketplace/featured-products", {
      params: {
        city_id: currentCity?.id || undefined,
        type: cfg.apiType,
        limit: 8,
      },
    })
      .then(({ data }) => setFeaturedProducts(Array.isArray(data) ? data : []))
      .catch(() => setFeaturedProducts([]));
  }, [cfg.apiType, currentCity?.id]);

  useEffect(() => {
    if (!selectedMerchant?.id) {
      setProducts([]);
      return;
    }
    api.get(`/merchants/${selectedMerchant.id}/products`)
      .then(({ data }) => {
        setProducts(Array.isArray(data) ? data : []);
        setCart({});
        setCheckoutOpen(false);
        setProductSearch("");
        setCategoryFilter("all");
      })
      .catch((err) => toast.error(formatApiError(err)));
  }, [selectedMerchant?.id]);

  const chooseMerchant = (merchantId) => {
    if (cartLines.length > 0 && merchantId !== selectedMerchantId) {
      toast.info("Le panier est limite a un seul partenaire. Panier vide pour changer de partenaire.");
      setCart({});
    }
    setSelectedMerchantId(merchantId);
    setLastOrder(null);
  };

  const setQuantity = (productId, quantity) => {
    if (!selectedMerchant) {
      toast.error("Choisissez d'abord un restaurant ou supermarche");
      return;
    }
    setCart((current) => {
      const next = { ...current };
      if (quantity <= 0) delete next[productId];
      else next[productId] = quantity;
      return next;
    });
  };

  const useCurrentLocation = async () => {
    setLocating(true);
    try {
      const location = await preciseCurrentAddress();
      setDeliveryAddress(location);
      toast.success("Position actuelle ajoutee comme adresse");
    } catch (err) {
      toast.error("Impossible de prendre votre position actuelle");
    } finally {
      setLocating(false);
    }
  };

  const saveDeliveryPlace = () => {
    if (!deliveryAddress?.lat || !deliveryAddress?.lng) {
      toast.error("Choisissez d'abord une adresse precise");
      return;
    }
    const item = {
      lat: Number(deliveryAddress.lat),
      lng: Number(deliveryAddress.lng),
      label: deliveryAddress.label || deliveryAddress.address || "Adresse sauvegardee",
      short: deliveryAddress.short || deliveryAddress.label || deliveryAddress.address || "Adresse sauvegardee",
      address: deliveryAddress.address,
      type: deliveryAddress.type,
    };
    try {
      const current = JSON.parse(localStorage.getItem(RECENT_PLACES_KEY)) || [];
      const next = [item, ...current.filter((p) => `${p.lat},${p.lng}` !== `${item.lat},${item.lng}`)].slice(0, 6);
      localStorage.setItem(RECENT_PLACES_KEY, JSON.stringify(next));
      toast.success("Adresse enregistree dans vos lieux");
    } catch {
      toast.error("Impossible d'enregistrer cette adresse");
    }
  };

  const submitOrder = async () => {
    if (!selectedMerchant) {
      toast.error("Choisissez un partenaire");
      return;
    }
    if (!deliveryAddress) {
      toast.error("Ajoutez l'adresse de livraison");
      return;
    }
    if (!recipientName.trim() || !recipientPhone.trim()) {
      toast.error("Nom et telephone du destinataire requis");
      return;
    }
    if (!cartLines.length) {
      toast.error("Ajoutez au moins un produit");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/merchant-orders", {
        merchant_id: selectedMerchant.id,
        items: cartLines.map((item) => ({ product_id: item.id, quantity: item.quantity })),
        delivery_address: deliveryAddress,
        recipient_name: recipientName.trim(),
        recipient_phone: recipientPhone.trim(),
        note: note.trim() || null,
        payment_method: paymentMethod,
        courier_vehicle_type: courierVehicleType,
      });
      toast.success(paymentMethod === "cash" ? "Commande envoyee au partenaire" : "Commande creee. Envoyez la capture de paiement.");
      setCart({});
      setCheckoutOpen(false);
      setLastOrder(data);
      setNote("");
      loadOrders();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const uploadPaymentProof = async (orderId, file, reference = "") => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    if (reference) form.append("reference", reference);
    setProofUploading(orderId);
    try {
      await api.post(`/merchant-orders/${orderId}/payment-proof`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Capture envoyee pour verification");
      loadOrders();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setProofUploading("");
    }
  };

  const startAutoPayment = async (order) => {
    const provider = order?.payment_method;
    if (!["mpesa", "airtel", "orange"].includes(provider)) {
      toast.error("Choisissez M-Pesa, Airtel ou Orange pour le paiement automatique");
      return;
    }
    const payerPhone = cleanPublicPhone(recipientPhone) || cleanPublicPhone(user?.phone);
    if (!payerPhone) {
      toast.error("Ajoutez votre numero de telephone avant de payer automatiquement");
      return;
    }
    setAutoPaying(order.id);
    try {
      const { data } = await api.post("/mobile-money/checkout", {
        target_type: "merchant_order",
        target_id: order.id,
        provider,
        phone: payerPhone,
        amount: order.total_amount,
      });
      if (data?.status === "SUCCEEDED") {
        toast.success("Paiement confirme automatiquement");
      } else {
        toast.message(data?.message || "Paiement en attente. Vous pouvez aussi envoyer une capture.");
      }
      setLastOrder(data?.target || order);
      loadOrders();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAutoPaying("");
    }
  };

  if (!currentCity) {
    return (
      <div className="phone-frame phone-scroll-safe bg-premium-grad">
        <div className="relative h-[42svh] min-h-[300px] overflow-hidden">
          <MapView center={DRC_CENTER} height="100%" zoom={DRC_MAP_ZOOM} showAttribution={false} showPoiLabels={false} />
          <div className="map-top-controls-safe absolute inset-x-0 z-[800] flex items-center justify-between px-4">
            <button onClick={() => navigate("/app/services")} className="rounded-full glass-strong p-3">
              <ArrowLeft size={18} weight="bold" />
            </button>
            <Badge tone="warning">RDC</Badge>
          </div>
        </div>
        <div className="-mt-10 px-4 pb-24">
          <Card className="relative z-[900] p-5">
            <Badge tone="warning">Ville requise</Badge>
            <h1 className="mt-3 font-heading text-2xl font-black">Choisissez votre ville</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Les restaurants et supermarches apparaissent uniquement apres selection de votre ville de service.
            </p>
            <Button className="mt-4 w-full" onClick={() => navigate("/app/profile")}>Aller au profil</Button>
          </Card>
        </div>
        <BottomNav role="customer" />
      </div>
    );
  }

  return (
    <div className="phone-frame phone-scroll-safe bg-premium-grad">
      <MobileHeader title={cfg.title} />
      <div className="space-y-4 px-4 pt-2">
        <Card className="overflow-hidden p-0">
          <div className="h-48">
            <MapView
              center={mapCenter}
              pickup={selectedMerchant ? { lat: selectedMerchant.lat, lng: selectedMerchant.lng } : null}
              destination={deliveryAddress}
              driverVehicleType={courierVehicleType}
              height="100%"
              zoom={mapZoom}
              showAttribution={false}
            />
          </div>
          <div className="p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="primary"><Icon size={12} weight="bold" /> {cfg.title}</Badge>
              <Badge tone="success"><Package size={12} weight="bold" /> Livraison Nexora</Badge>
            </div>
            <h1 className="mt-3 font-heading text-2xl font-black">{cfg.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{cfg.subtitle}</p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Link to="/app/delivery" className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-2 text-[11px] font-black uppercase tracking-widest">
                <Package size={16} weight="bold" /> Colis
              </Link>
              <Link to="/app/services" className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-primary px-3 py-2 text-[11px] font-black uppercase tracking-widest text-primary-foreground">
                Services
              </Link>
            </div>
          </div>
        </Card>

        <div className="flex gap-2 overflow-x-auto pb-1">
          {merchants.map((merchant) => (
            <button
              key={merchant.id}
              onClick={() => chooseMerchant(merchant.id)}
              className={`min-w-[180px] rounded-2xl border p-3 text-left press-down ${selectedMerchant?.id === merchant.id ? "border-primary/60 bg-primary/15" : "border-white/10 bg-white/5"}`}
            >
              <div className="flex items-center gap-2">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary">
                  <Icon size={20} weight="duotone" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate font-heading text-sm font-black">{merchant.name}</span>
                  <span className="block truncate text-[10px] text-muted-foreground">{merchant.address}</span>
                </span>
              </div>
              <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
                <span>{merchant.product_count || 0} produits</span>
                <span>{merchant.preparation_time_min || 20} min</span>
              </div>
            </button>
          ))}
        </div>

        {merchants.length === 0 && (
          <Card className="p-6 text-center text-sm text-muted-foreground">
            {cfg.empty} L'admin peut ajouter des partenaires dans Marketplace.
          </Card>
        )}

        {merchants.length > 0 && !selectedMerchant && (
          <Card className="p-5 text-center">
            <Badge tone="primary">Etape 1</Badge>
            <h2 className="mt-3 font-heading text-2xl font-black">Choisissez un partenaire</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Selectionnez un seul {cfg.apiType === "restaurant" ? "restaurant" : "supermarche"} pour voir ses produits et commander sans melanger plusieurs boutiques.
            </p>
          </Card>
        )}

        {selectedMerchant && (
          <Card className="overflow-hidden p-0">
            {(selectedMerchant.cover_url || selectedMerchant.logo_url) && (
              <div className="h-32 bg-white/5">
                <img
                  src={selectedMerchant.cover_url || selectedMerchant.logo_url}
                  alt=""
                  className="h-full w-full object-cover"
                />
              </div>
            )}
            <div className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-primary">{selectedMerchant.city_name}</div>
                <h2 className="font-heading text-xl font-black">{selectedMerchant.name}</h2>
                <p className="mt-1 text-xs text-muted-foreground">{selectedMerchant.description || selectedMerchant.address}</p>
              </div>
              <Badge tone="success">Ouvert</Badge>
            </div>
            </div>
          </Card>
        )}

        {featuredProducts.length > 0 && (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="font-heading text-lg font-black">Pubs produits</h2>
              <Badge tone="primary">Aleatoire</Badge>
            </div>
            <div className="product-ad-marquee overflow-hidden pb-2">
              <div className="product-ad-marquee-track flex w-max gap-3">
              {[...featuredProducts, ...featuredProducts].map((product, index) => (
                <button
                  key={`featured-${product.id}-${index}`}
                  type="button"
                  onClick={() => {
                    if (product.merchant_id) chooseMerchant(product.merchant_id);
                    setProductSearch(product.name || "");
                  }}
                  className="w-[172px] shrink-0 overflow-hidden rounded-3xl border border-white/10 bg-white/5 text-left press-down min-[390px]:w-[188px]"
                >
                  <div className="h-28 overflow-hidden bg-black/20">
                    {product.image_url ? (
                      <img src={product.image_url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-primary"><ShoppingCart size={30} weight="duotone" /></div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="line-clamp-2 min-h-[2.15rem] font-heading text-sm font-black leading-tight">{product.name}</div>
                    <div className="mt-1 truncate text-[10px] text-muted-foreground">{product.merchant_name || selectedMerchant?.name || "Partenaire Nexora"}</div>
                    <div className="mt-2 whitespace-nowrap font-mono text-[12px] font-black text-primary">{formatCDF(product.price)}</div>
                  </div>
                </button>
              ))}
              </div>
            </div>
          </div>
        )}

        {selectedMerchant && (
          <AiInsightCard
            endpoint="/ai/assistant"
            title="IA marketplace"
            limit={2}
            compact
          />
        )}

        {selectedMerchant && (
          <Card className="p-3">
            <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
              <div className="flex items-center gap-2">
                <MagnifyingGlass size={18} weight="bold" className="shrink-0 text-muted-foreground" />
                <Input
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder={`Rechercher chez ${selectedMerchant.name}...`}
                  className="border-transparent bg-transparent"
                />
              </div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="rounded-xl border border-white/10 bg-[#15171b] px-3 py-2 text-xs font-bold text-white [color-scheme:dark]"
              >
                <option value="all">Toutes categories</option>
                {productCategories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </Card>
        )}

        {selectedMerchant && (
        <div className="grid grid-cols-1 gap-3 min-[430px]:grid-cols-2">
          {filteredProducts.map((product) => {
            const quantity = qtyInCart(cart, product.id);
            return (
              <Card key={product.id} className="overflow-hidden p-0">
                <div className="flex min-h-[150px] flex-col">
                  <div className="h-28 bg-white/5 text-primary">
                    {product.image_url ? (
                      <img src={product.image_url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center"><ShoppingCart size={34} weight="duotone" /></div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-3">
                    <div className="text-[10px] font-black uppercase tracking-widest text-primary">{product.merchant_name || selectedMerchant.name}</div>
                    <div className="mt-1 line-clamp-2 font-heading text-base font-black">{product.name}</div>
                    <div className="mt-1 line-clamp-2 min-h-[32px] text-xs text-muted-foreground">{product.description || product.category || "Produit partenaire"}</div>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <div className="font-mono text-sm font-black text-primary">{formatCDF(product.price)}</div>
                      <div className="flex shrink-0 items-center gap-1">
                        {quantity > 0 && (
                          <button type="button" onClick={() => setQuantity(product.id, quantity - 1)} className="rounded-full bg-white/10 p-2">
                            <Minus size={14} weight="bold" />
                          </button>
                        )}
                        {quantity > 0 && <span className="w-7 text-center font-black">{quantity}</span>}
                        <button type="button" onClick={() => setQuantity(product.id, quantity + 1)} className="rounded-full bg-primary p-2 text-primary-foreground" aria-label={`Ajouter ${product.name}`}>
                          <Plus size={14} weight="bold" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
          {products.length > 0 && filteredProducts.length === 0 && (
            <Card className="p-6 text-center text-sm text-muted-foreground">
              Aucun produit ne correspond a cette recherche.
            </Card>
          )}
        </div>
        )}

        {cartLines.length > 0 && (
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-xl font-black">Panier</h2>
                <p className="text-xs text-muted-foreground">{selectedMerchant?.name}</p>
              </div>
              <button onClick={() => setCart({})} className="rounded-full bg-white/10 p-2 text-muted-foreground">
                <Trash size={16} weight="bold" />
              </button>
            </div>
            <div className="space-y-2">
              {cartLines.map((item) => (
                <div key={item.id} className="flex justify-between gap-3 text-sm">
                  <span>{item.quantity} x {item.name}</span>
                  <span className="font-mono font-black">{formatCDF(item.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-3">
              <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Sous-total produits</span>
              <span className="font-mono text-lg font-black">{formatCDF(subtotal)}</span>
            </div>
            <Button onClick={() => setCheckoutOpen(true)} className="mt-4 w-full">
              <ShoppingCart size={18} weight="bold" /> Ouvrir le panier
            </Button>
          </Card>
        )}

        <div>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-heading text-xl font-black">Mes commandes</h2>
            <Badge>{orders.length}</Badge>
          </div>
          <div className="space-y-2">
            {orders.slice(0, 8).map((order) => (
              <Card key={order.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate font-heading text-base font-black">{order.merchant_name}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {order.items?.length || 0} article(s) - {formatCDF(order.total_amount)}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <StatusBadge status={order.status} />
                      <StatusBadge status={order.payment_status || "PENDING"} />
                      {order.ride_id && <Badge tone="primary">Livraison creee</Badge>}
                    </div>
                    <PaymentProofBox
                      order={order}
                      uploading={proofUploading === order.id}
                      autoPaying={autoPaying === order.id}
                      onUpload={uploadPaymentProof}
                      onAutoPay={startAutoPayment}
                    />
                  </div>
                  {order.ride_id ? (
                    <Link to={`/app/ride/${order.ride_id}`} className="rounded-full bg-primary px-3 py-2 text-[10px] font-black uppercase tracking-widest text-primary-foreground">
                      Suivre
                    </Link>
                  ) : (
                    <MapPin size={20} className="text-muted-foreground" />
                  )}
                </div>
              </Card>
            ))}
            {orders.length === 0 && (
              <Card className="p-5 text-center text-xs text-muted-foreground">Aucune commande marketplace.</Card>
            )}
          </div>
        </div>

        <Link to="/app/services">
          <Button variant="outline" className="w-full"><ArrowLeft size={18} weight="bold" /> Retour services</Button>
        </Link>
      </div>

      {checkoutOpen && cartLines.length > 0 && (
        <div className="fixed inset-0 z-[2200] flex items-end justify-center bg-black/70 p-3 backdrop-blur-sm min-[640px]:items-center">
          <div className="max-h-[88dvh] w-full max-w-[520px] overflow-y-auto rounded-[28px] border border-white/10 bg-[#111217] p-4 shadow-2xl shadow-black/50">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <Badge tone="primary">Panier</Badge>
                <h2 className="mt-2 font-heading text-2xl font-black">Confirmer commande</h2>
                <p className="text-xs text-muted-foreground">{selectedMerchant?.name}</p>
              </div>
              <button type="button" onClick={() => setCheckoutOpen(false)} className="rounded-full bg-white/10 p-2 text-muted-foreground">
                <X size={18} weight="bold" />
              </button>
            </div>

            <div className="mb-4 space-y-2 rounded-2xl border border-white/10 bg-white/5 p-3">
              {cartLines.map((item) => (
                <div key={item.id} className="flex justify-between gap-3 text-sm">
                  <span>{item.quantity} x {item.name}</span>
                  <span className="font-mono font-black">{formatCDF(item.line_total)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-white/10 pt-3">
                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Sous-total</span>
                <span className="font-mono text-lg font-black text-primary">{formatCDF(subtotal)}</span>
              </div>
            </div>

            <div className="grid gap-3">
              <div>
                <Label>Adresse de livraison</Label>
                <AddressSearch
                  cityCenter={cityCenter}
                  placeholder="Ou livrer la commande ?"
                  valueLabel={deliveryAddress?.short || deliveryAddress?.address || ""}
                  onSelect={(r) => setDeliveryAddress({ lat: r.lat, lng: r.lng, address: r.label, map_data: r })}
                />
                <div className="mt-2 grid gap-2 min-[380px]:grid-cols-2">
                  <button
                    type="button"
                    onClick={useCurrentLocation}
                    disabled={locating}
                    className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-primary/40 bg-primary/10 px-3 py-2 text-xs font-black uppercase tracking-widest text-primary disabled:opacity-60"
                  >
                    <Crosshair size={16} weight="bold" /> {locating ? "Localisation..." : "Position actuelle"}
                  </button>
                  <button
                    type="button"
                    onClick={saveDeliveryPlace}
                    className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-black uppercase tracking-widest"
                  >
                    <FloppyDisk size={16} weight="bold" /> Sauvegarder lieu
                  </button>
                </div>
                {deliveryAddress && (
                  <div className="mt-2 rounded-xl border border-white/10 bg-white/5 p-2 text-[11px] text-muted-foreground">
                    {deliveryAddress.address || deliveryAddress.label || `${deliveryAddress.lat}, ${deliveryAddress.lng}`}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Destinataire</Label>
                  <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder={user?.name || "Nom"} />
                </div>
                <div>
                  <Label>Telephone</Label>
                  <Input value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)} placeholder={cleanPublicPhone(user?.phone) || "+243..."} inputMode="tel" />
                </div>
              </div>
              <div>
                <Label>Note livraison</Label>
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Maison, bureau, reference..." />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setCourierVehicleType("moto")} className={`rounded-2xl border p-3 text-left ${courierVehicleType === "moto" ? "border-primary/60 bg-primary/15" : "border-white/10 bg-white/5"}`}>
                  <Motorcycle size={20} weight="duotone" /> <span className="ml-1 font-bold">Moto</span>
                </button>
                <button type="button" onClick={() => setCourierVehicleType("economy")} className={`rounded-2xl border p-3 text-left ${courierVehicleType === "economy" ? "border-primary/60 bg-primary/15" : "border-white/10 bg-white/5"}`}>
                  <Car size={20} weight="duotone" /> <span className="ml-1 font-bold">Voiture</span>
                </button>
              </div>
              <div>
                <Label>Paiement</Label>
                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full rounded-xl border border-white/10 bg-background px-4 py-3 text-sm text-foreground">
                  <option value="cash">Cash a la livraison</option>
                  <option value="mpesa">M-Pesa direct</option>
                  <option value="airtel">Airtel Money direct</option>
                  <option value="orange">Orange Money direct</option>
                </select>
              </div>
              {paymentMethod !== "cash" && (
                <div className={`rounded-2xl border p-3 text-sm ${paymentPhone ? "border-primary/30 bg-primary/10" : "border-destructive/30 bg-destructive/10"}`}>
                  <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Paiement partenaire</div>
                  {paymentPhone ? (
                    <>
                      <div className="mt-1 font-heading text-lg font-black">{paymentPhone}</div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Apres creation de la commande, vous pourrez lancer le paiement automatique. Si le provider n'est pas encore connecte, envoyez la capture pour verification.
                      </p>
                      {selectedMerchant?.payment_instructions && (
                        <p className="mt-2 rounded-xl bg-white/10 p-2 text-xs">{selectedMerchant.payment_instructions}</p>
                      )}
                    </>
                  ) : (
                    <p className="mt-1 text-xs text-destructive">Ce partenaire n'a pas encore configure ce moyen de paiement.</p>
                  )}
                </div>
              )}
              <Button onClick={submitOrder} disabled={submitting} className="w-full">
                <ShoppingCart size={18} weight="bold" /> {submitting ? "Envoi..." : "Commander"}
              </Button>
              <p className="text-[11px] text-muted-foreground">
                Le partenaire confirme d'abord la disponibilite. Ensuite Nexora cherche un livreur et vous suivez la livraison comme une course.
              </p>
            </div>
          </div>
        </div>
      )}

      {lastOrder && (
        <div className="fixed inset-0 z-[2200] flex items-end justify-center bg-black/70 p-3 backdrop-blur-sm min-[640px]:items-center">
          <div className="w-full max-w-[480px] rounded-[28px] border border-white/10 bg-[#111217] p-4 shadow-2xl shadow-black/50">
            <div className="flex items-start justify-between gap-3">
              <div>
                <Badge tone="success">Commande envoyee</Badge>
                <h2 className="mt-3 font-heading text-2xl font-black">{lastOrder.merchant_name}</h2>
              </div>
              <button type="button" onClick={() => setLastOrder(null)} className="rounded-full bg-white/10 p-2 text-muted-foreground">
                <X size={18} weight="bold" />
              </button>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Le partenaire confirme la disponibilite. Si besoin, contactez-le avec le numero configure.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <StatusBadge status={lastOrder.status} />
              <Badge>{formatCDF(lastOrder.total_amount)}</Badge>
            </div>
            <div className="mt-4 grid gap-2 min-[390px]:grid-cols-2">
              {lastOrder.payment_method !== "cash" && (
                <button
                  type="button"
                  onClick={() => startAutoPayment(lastOrder)}
                  disabled={autoPaying === lastOrder.id}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-secondary px-4 py-2 text-sm font-black text-secondary-foreground disabled:opacity-60"
                >
                  {autoPaying === lastOrder.id ? "Paiement..." : "Payer automatiquement"}
                </button>
              )}
              {cleanPublicPhone(lastOrder.merchant_phone) && (
                <a href={`tel:${cleanPublicPhone(lastOrder.merchant_phone)}`} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-black text-primary-foreground">
                  <Phone size={18} weight="fill" /> Appeler partenaire
                </a>
              )}
              {lastOrder.ride_id && (
                <Link to={`/app/ride/${lastOrder.ride_id}`} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-black">
                  Suivre livraison
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      <BottomNav role="customer" />
    </div>
  );
}
